import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import account from '../services/account-service';

const PREFIX = 'ResetPasswordPage';
const classes = {
  root: `${PREFIX}-root`,
  card: `${PREFIX}-card`,
};
const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0F1117',
  },
  [`& .${classes.card}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    backgroundColor: '#1a1d27',
    borderRadius: theme.shape.borderRadius,
    width: '100%',
    maxWidth: 400,
  },
}));

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset link. Please request a new one.');
    }
  }, [token]);

  const handleSubmit = async () => {
    setError('');

    if (!token) {
      setError('Invalid or missing reset link. Please request a new one.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await account.resetPassword({ token, password });
      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(response.reason || 'Failed to reset password. The link may be expired or already used.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Root className={classes.root}>
      <Box className={classes.card}>
        <Typography variant="h5" color="white" fontWeight={600}>
          Reset Password
        </Typography>

        {success ? (
          <>
            <Alert severity="success" sx={{ width: '100%' }}>
              Your password has been reset successfully. Redirecting to login…
            </Alert>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              color="grey.400"
              textAlign="center"
            >
              Enter your new password below.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              id="password"
              label="New Password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading || !token}
            />

            <TextField
              fullWidth
              id="confirmPassword"
              label="Confirm New Password"
              type="password"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading || !token}
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !token}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
            </Button>
          </>
        )}

        <Button
          variant="text"
          color="inherit"
          size="small"
          sx={{ color: 'grey.400' }}
          onClick={() => navigate('/login')}
        >
          Back to Login
        </Button>
      </Box>
    </Root>
  );
}
